import React from "react";
import { Heart } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useRetailer } from "../../hooks/useRetailer";
import {
  useAddFavoriteWithRetailer,
  useRemoveFavoriteWithRetailer,
  useAddWalmartOnlineFavorite,
} from "../../hooks/useFavorites";
import { useFavoritesContext } from "../../context/FavoritesContext";
import { useUserContext } from "../../context/UserContext";
import {
  buildFavoritePayload,
  buildWalmartOnlinePayload,
  getIsActionLoading,
} from "./SaveButton.utils";
import { isFreeTier } from "../../utils/userUtils";
import { isOnlineOnlyRetailer } from "../../config/retailers";
import {
  getAllFavorites,
  globalFavoritesQueryKey,
} from "../../services/GlobalFavoritesService";

// Full product data for walmart_online
interface WalmartOnlineProductData {
  msrp: number;
  currentPrice: number;
  currentPrices: number[];
  url: string;
  sku: string;
  lastUpdated: number;
  lastUpdates: number[];
  discountPercentage: number;
}

interface SaveButtonProps {
  productId: string;
  productName: string;
  productImage?: string;
  productCategory?: string;
  productStoreId?: string;
  className?: string;
  compact?: boolean;
  walmartOnlineData?: WalmartOnlineProductData;
  // Snapshot of the product at save time (price/stock) for the favorite record.
  onlinePrice?: number;
  storePrice?: number;
  stock?: number;
  /** Retailer for mixed-retailer surfaces such as Best Deals. */
  retailerOverride?: string;
  onFavoriteAdded?: () => void;
}

const SaveButton: React.FC<SaveButtonProps> = ({
  productId,
  productName,
  productImage,
  productCategory,
  productStoreId,
  className = "",
  compact = false,
  walmartOnlineData,
  onlinePrice,
  storePrice,
  stock,
  retailerOverride,
  onFavoriteAdded,
}) => {
  const routeRetailer = useRetailer();
  const currentRetailer = retailerOverride || routeRetailer;
  const location = useLocation();
  const isWalmartOnline = currentRetailer === "walmart_online";
  const isOnlineMode =
    productStoreId === "online" ||
    (!retailerOverride && new URLSearchParams(location.search).get("storeId") === "online") ||
    isOnlineOnlyRetailer(currentRetailer);
  const { user } = useUserContext();

  // Get favorite status from context
  const { isFavorite: checkIsFavorite, isLoading: isCheckingCurrentFavorite } =
    useFavoritesContext();
  const globalFavoritesQuery = useQuery({
    queryKey: globalFavoritesQueryKey,
    queryFn: getAllFavorites,
    enabled: Boolean(retailerOverride && user && !isFreeTier(user)),
    staleTime: 60 * 1000,
  });
  const isFavorite = retailerOverride
    ? (globalFavoritesQuery.data || []).some(
        (favorite) => favorite.retailer === currentRetailer && favorite.upc === productId,
      )
    : checkIsFavorite(productId);
  const isCheckingFavorite = retailerOverride
    ? globalFavoritesQuery.isLoading
    : isCheckingCurrentFavorite;

  // Favorite functionality - use basic=true to match the context, showSuccessToast=false to suppress success toasts
  const addFavoriteMutation = useAddFavoriteWithRetailer(
    currentRetailer,
    true,
    false
  );
  const addWalmartOnlineMutation = useAddWalmartOnlineFavorite(false);
  const removeFavoriteMutation = useRemoveFavoriteWithRetailer(
    currentRetailer,
    true,
    false
  );

  // Handle favorite toggle
  const handleFavoriteToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      if (isFavorite) {
        await removeFavoriteMutation.mutateAsync(productId);
        return;
      }
      if (isWalmartOnline && walmartOnlineData) {
        await addWalmartOnlineMutation.mutateAsync(
          buildWalmartOnlinePayload({
            productId,
            productName,
            productImage,
            productCategory,
            walmartOnlineData,
          })
        );
        onFavoriteAdded?.();
        return;
      }
      await addFavoriteMutation.mutateAsync(
        buildFavoritePayload({
          productId,
          productName,
          productImage,
          productCategory,
          productStoreId: isOnlineMode ? "online" : productStoreId,
          onlinePrice,
          storePrice,
          stock,
        })
      );
      onFavoriteAdded?.();
    } catch {
      // Mutation hooks surface request failures to the user.
    }
  };

  // Only show spinner for user actions, not initial check
  const isActionLoading = getIsActionLoading({
    addPending: addFavoriteMutation.isPending,
    addOnlinePending: addWalmartOnlineMutation.isPending,
    removePending: removeFavoriteMutation.isPending,
  });
  const isDisabled = isCheckingFavorite || isActionLoading;

  // Free accounts can browse the complete online catalog, but saving is not
  // part of the free web experience. Keep this guard in the shared button so
  // cards, galleries, and detail entry points cannot accidentally re-enable it.
  if (isFreeTier(user)) return null;

  return (
    <button
        onClick={handleFavoriteToggle}
        disabled={isDisabled}
        className={`group relative inline-flex items-center ${
          compact ? "p-1.5 rounded-full" : "gap-1.5 px-2.5 py-1.5 rounded-lg"
        } text-xs font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
          isActionLoading
            ? "bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 cursor-not-allowed"
            : isFavorite
            ? "bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 hover:bg-rose-200 dark:hover:bg-rose-900/60 hover:shadow-sm active:scale-95"
            : "bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/40 hover:shadow-sm active:scale-95"
        } ${className}`}
        title={isFavorite ? "Remove from saved" : "Add to saved"}
      >
        {isActionLoading ? (
          <>
            <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            {!compact && <span>{isFavorite ? "Removing..." : "Adding..."}</span>}
          </>
        ) : (
          <>
            <Heart
              className={`w-3.5 h-3.5 transition-all duration-200 ${
                isFavorite ? "fill-current scale-110" : "group-hover:scale-110"
              }`}
            />
            {!compact && <span>{isFavorite ? "Saved" : "Save"}</span>}
          </>
        )}

        <div
          className={`absolute inset-0 ${
            compact ? "rounded-full" : "rounded-lg"
          } transition-all duration-200 ${
            isFavorite
              ? "bg-gradient-to-r from-rose-600/0 to-rose-700/0 group-hover:from-rose-600/5 group-hover:to-rose-700/5"
              : "bg-gradient-to-r from-rose-500/0 to-rose-600/0 group-hover:from-rose-500/5 group-hover:to-rose-600/5"
          }`}
        />
    </button>
  );
};

export default SaveButton;
